import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import ko from "./messages/ko.json";
import en from "./messages/en.json";

/**
 * i18n 층 (SR-NFR-11 — ko 기본 · en 준비). Bench shared/i18n(@62f4f03)과 같은 모양: 키로 부르고 팩이 답한다,
 * 대체 팩 → 기준 팩(ko) → 키 그대로 + 경고(W-MSG-001). Field 차이: 로캘은 세션(LTI)에서 정하고 React 컨텍스트로 전파한다. 문구는 messages/*.json 카탈로그 키로만 쓴다.
 * 학생 화면 문구는 `common.*` · `student.*` · `fe.*.student.*` 에만 두며 DR-01/02 검사 대상이다.
 */
export type Locale = "ko" | "en";
export const LOCALES: readonly Locale[] = ["ko", "en"];
export const DEFAULT_LOCALE: Locale = "ko";

type Tree = { [k: string]: string | Tree };

export function flatten(tree: Tree, prefix = "", out: Record<string, string> = {}) {
  for (const [k, v] of Object.entries(tree)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") out[key] = v;
    else flatten(v, key, out);
  }
  return out;
}

export const catalogs: Record<Locale, Record<string, string>> = {
  ko: flatten(ko as Tree),
  en: flatten(en as Tree),
};

/** "ko-KR" · "en_US" → 지원 로캘, 모르면 ko */
export function resolveLocale(raw: string | null | undefined): Locale {
  const base = (raw ?? "").toLowerCase().split(/[-_]/)[0];
  return (LOCALES as readonly string[]).includes(base ?? "") ? (base as Locale) : DEFAULT_LOCALE;
}

export type Vars = Record<string, string | number>;
export type Translate = (key: string, vars?: Vars) => string;

export function createTranslator(locale: Locale): Translate {
  const primary = catalogs[locale];
  const fallback = catalogs[DEFAULT_LOCALE];
  return (key, vars) => {
    const msg = primary[key] ?? fallback[key];
    if (msg == null) {
      // Bench shared/i18n 과 같은 규칙: 빈 칸 대신 키를 보이고 경고를 남긴다(조용히 사라지면 아무도 못 고친다)
      console.warn("W-MSG-001 없는 문자열 키", key);
      return key;
    }
    return vars ? msg.replace(/\{(\w+)\}/g, (m, k: string) => String(vars[k] ?? m)) : msg;
  };
}

/** 키가 카탈로그에 있고 빈 문자열이 아닌지 */
export function hasMessage(key: string, locale: Locale = DEFAULT_LOCALE): boolean {
  return !!(catalogs[locale][key] ?? catalogs[DEFAULT_LOCALE][key]);
}

interface I18nCtx {
  locale: Locale;
  t: Translate;
}

const Ctx = createContext<I18nCtx>({
  locale: DEFAULT_LOCALE,
  t: createTranslator(DEFAULT_LOCALE),
});

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const value = useMemo(() => ({ locale, t: createTranslator(locale) }), [locale]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  return useContext(Ctx);
}

export function useT(): Translate {
  return useContext(Ctx).t;
}
